'use client';

/**
 * Marketing-Event-Layer für die Landing-Pages (athlyn.de).
 *
 * Zweck: ein dünner, DSGVO-konformer Wrapper über Meta- (Facebook-) und
 * TikTok-Pixel. Pixel werden ERST nach explizitem Consent geladen
 * (Opt-in, siehe ConsentBanner). Vor Consent passiert nichts — keine
 * Netzwerk-Requests, keine Cookies, keine Device-IDs.
 *
 * Pixel-IDs kommen ausschließlich aus NEXT_PUBLIC_*-Env-Vars, nie
 * hardcoded:
 *   - NEXT_PUBLIC_META_PIXEL_ID
 *   - NEXT_PUBLIC_TIKTOK_PIXEL_ID
 *
 * Ohne gesetzte Env-Var wird das jeweilige Pixel nicht initialisiert —
 * der Code bleibt damit auch ohne IDs deploy-bar (Platzhalter-tolerant).
 *
 * Event-Modell (provider-agnostisch):
 *   - cta_click        — Klick auf einen primären CTA (mit `location`)
 *   - app_store_click  — Klick auf das App-Store-Badge
 *   - app_open         — „Im Browser testen" / „Web öffnen"
 *   - lead             — B2B: „Demo/Discovery-Call anfragen"
 *   - scroll_depth     — 25 / 50 / 75 / 100 % Scroll-Tiefe (einmalig)
 *
 * Diese internen Namen werden auf die Standard-Events der Pixel gemappt
 * (Meta: Lead/ViewContent, TikTok: ClickButton/SubmitForm), damit die
 * Werbe-Plattformen sinnvoll optimieren können.
 */

export const CONSENT_KEY = 'athlyn_consent_v1';

export type ConsentState = 'granted' | 'denied' | 'unset';

export type MarketingEvent =
  | 'cta_click'
  | 'app_store_click'
  | 'app_open'
  | 'lead'
  | 'scroll_depth';

const META_PIXEL_ID =
  typeof process !== 'undefined' ? process.env.NEXT_PUBLIC_META_PIXEL_ID : undefined;
const TIKTOK_PIXEL_ID =
  typeof process !== 'undefined' ? process.env.NEXT_PUBLIC_TIKTOK_PIXEL_ID : undefined;

interface WindowWithPixels extends Window {
  fbq?: (...args: unknown[]) => void;
  _fbq?: unknown;
  ttq?: {
    page: () => void;
    track: (name: string, props?: Record<string, unknown>) => void;
    load: (id: string) => void;
    methods?: string[];
    setAndDefer?: (t: unknown, m: string) => void;
    instance?: (id: string) => unknown;
    _i?: Record<string, unknown>;
    _t?: Record<string, number>;
    _o?: Record<string, unknown>;
    push?: (...a: unknown[]) => void;
    [k: string]: unknown;
  };
}

let pixelsInitialized = false;

// ── Consent-State lesen/schreiben ────────────────────────
export function getConsent(): ConsentState {
  if (typeof window === 'undefined') return 'unset';
  try {
    const v = window.localStorage.getItem(CONSENT_KEY);
    return v === 'granted' || v === 'denied' ? v : 'unset';
  } catch {
    return 'unset';
  }
}

export function setConsent(state: 'granted' | 'denied'): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(CONSENT_KEY, state);
  } catch {
    /* private mode / storage disabled — fail closed (no tracking) */
  }
  if (state === 'granted') initPixels();
}

// ── Pixel-Loader (erst nach Consent) ─────────────────────
export function initPixels(): void {
  if (typeof window === 'undefined') return;
  if (pixelsInitialized) return;
  if (getConsent() !== 'granted') return;
  pixelsInitialized = true;

  if (META_PIXEL_ID) loadMetaPixel(META_PIXEL_ID);
  if (TIKTOK_PIXEL_ID) loadTikTokPixel(TIKTOK_PIXEL_ID);
}

function loadMetaPixel(id: string): void {
  const w = window as WindowWithPixels;
  if (w.fbq) return;
  /* eslint-disable */
  const n: any = (w.fbq = function (...args: unknown[]) {
    n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
  });
  if (!w._fbq) w._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];
  const s = document.createElement('script');
  s.async = true;
  s.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(s);
  /* eslint-enable */
  w.fbq!('init', id);
  w.fbq!('track', 'PageView');
}

function loadTikTokPixel(id: string): void {
  const w = window as WindowWithPixels;
  if (w.ttq) return;
  /* eslint-disable */
  (function (win: any, doc: Document) {
    win.TiktokAnalyticsObject = 'ttq';
    const ttq: any = (win.ttq = win.ttq || []);
    ttq.methods = [
      'page', 'track', 'identify', 'instances', 'debug', 'on', 'off',
      'once', 'ready', 'alias', 'group', 'enableCookie', 'disableCookie',
    ];
    ttq.setAndDefer = function (t: any, e: string) {
      t[e] = function (...args: unknown[]) {
        t.push([e].concat(Array.prototype.slice.call(args, 0)));
      };
    };
    for (let i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
    ttq.instance = function (t: string) {
      const e = ttq._i[t] || [];
      for (let n = 0; n < ttq.methods.length; n++) ttq.setAndDefer(e, ttq.methods[n]);
      return e;
    };
    ttq.load = function (e: string, n?: unknown) {
      const r = 'https://analytics.tiktok.com/i18n/pixel/events.js';
      ttq._i = ttq._i || {};
      ttq._i[e] = [];
      ttq._i[e]._u = r;
      ttq._t = ttq._t || {};
      ttq._t[e] = +new Date();
      ttq._o = ttq._o || {};
      ttq._o[e] = n || {};
      const o = doc.createElement('script');
      o.type = 'text/javascript';
      o.async = true;
      o.src = r + '?sdkid=' + e + '&lib=ttq';
      const a = doc.getElementsByTagName('script')[0];
      a.parentNode?.insertBefore(o, a);
    };
    ttq.load(id);
    ttq.page();
  })(window, document);
  /* eslint-enable */
}

// ── Event-Tracking (provider-agnostisch) ─────────────────
export function trackEvent(event: MarketingEvent, props: Record<string, unknown> = {}): void {
  if (typeof window === 'undefined') return;
  if (getConsent() !== 'granted') return;
  // Lazy-init falls Consent zuvor in einem anderen Tab gesetzt wurde.
  if (!pixelsInitialized) initPixels();

  const w = window as WindowWithPixels;

  // Meta-Mapping
  if (w.fbq) {
    switch (event) {
      case 'lead':
        w.fbq('track', 'Lead', props);
        break;
      case 'app_store_click':
      case 'app_open':
        w.fbq('track', 'ViewContent', { content_name: event, ...props });
        break;
      default:
        w.fbq('trackCustom', event, props);
    }
  }

  // TikTok-Mapping
  if (w.ttq?.track) {
    switch (event) {
      case 'lead':
        w.ttq.track('SubmitForm', props);
        break;
      case 'cta_click':
      case 'app_store_click':
      case 'app_open':
        w.ttq.track('ClickButton', { content_name: event, ...props });
        break;
      default:
        w.ttq.track(event, props);
    }
  }
}
